/**
 * Domain Command Service
 * 
 * Application service for domain write operations following CQRS pattern.
 * Encapsulates business use cases and orchestrates domain operations.
 * 
 * FIXED: No more Inversify - direct constructor injection following party-manager pattern.
 */

import { Domain, DomainProperties } from '../../domain/entities/Domain';
import type { IDomainRepository } from '../../../meta-model-design/domain/ports/IDomainRepository';
import { IEventBus } from '@swoft/core';
import { DomainCreatedEvent } from '../../domain/events/DomainCreatedEvent';
import { DomainUpdatedEvent } from '../../domain/events/DomainUpdatedEvent';
import { CreateDomainPayload } from '../commands/CreateDomainCommand';
import { UpdateDomainPayload } from '../commands/UpdateDomainCommand';

export interface CreateDomainResult {
  domainId: string;
  success: boolean;
  message: string;
}

export interface UpdateDomainResult {
  success: boolean;
  message: string;
}

export class DomainCommandService {
  constructor(
    private readonly domainRepository: IDomainRepository,
    private readonly eventBus: IEventBus
  ) {}

  async createDomain(
    payload: CreateDomainPayload,
    authSessionId: string,
    initiatedByPersonId: string,
    correlationId?: string
  ): Promise<CreateDomainResult> {
    // Check for duplicate domain names
    const existingDomain = await this.domainRepository.findByName(payload.name);
    if (existingDomain) {
      throw new Error(`Domain with name "${payload.name}" already exists`);
    }

    // Create domain entity with business logic validation
    const domain = Domain.create({
      name: payload.name,
      description: payload.description,
      vision: payload.vision,
      ubiquitousLanguage: payload.ubiquitousLanguage || {}
    });

    // Persist to repository
    await this.domainRepository.save(domain);

    // Publish domain created event
    const domainCreatedEvent = new DomainCreatedEvent(
      domain.id,
      domain.name,
      domain.description,
      authSessionId,
      initiatedByPersonId,
      correlationId
    );

    await this.eventBus.publish(domainCreatedEvent);

    return {
      domainId: domain.id,
      success: true,
      message: `Domain "${payload.name}" created successfully with ID ${domain.id}`
    };
  }

  async updateDomain(
    domainId: string,
    payload: UpdateDomainPayload,
    authSessionId: string,
    initiatedByPersonId: string,
    correlationId?: string
  ): Promise<UpdateDomainResult> {
    // Find existing domain
    const domain = await this.domainRepository.findById(domainId);
    if (!domain) {
      throw new Error(`Domain with ID ${domainId} not found`);
    }

    const previousState = domain.toJSON();

    // Apply updates
    if (payload.name !== undefined) {
      domain.updateName(payload.name);
    }

    if (payload.description !== undefined) {
      domain.updateDescription(payload.description);
    }

    if (payload.vision !== undefined) {
      domain.updateVision(payload.vision);
    }

    if (payload.ubiquitousLanguage) {
      // For simplicity, we'll replace the entire ubiquitous language
      // In a real implementation, you might want more granular updates
      Object.entries(payload.ubiquitousLanguage).forEach(([term, definition]) => {
        domain.addUbiquitousLanguageTerm(term, definition);
      });
    }

    // Persist changes
    await this.domainRepository.save(domain);

    // Publish domain updated event
    const domainUpdatedEvent = new DomainUpdatedEvent(
      domain.id,
      domain.name,
      previousState,
      domain.toJSON(),
      authSessionId,
      initiatedByPersonId,
      correlationId
    );

    await this.eventBus.publish(domainUpdatedEvent);

    return {
      success: true,
      message: `Domain "${domain.name}" updated successfully`
    };
  }

  async deleteDomain(domainId: string): Promise<void> {
    const domain = await this.domainRepository.findById(domainId);
    if (!domain) {
      throw new Error(`Domain not found: ${domainId}`);
    }

    await this.domainRepository.deleteById(domainId);
  }
}